import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  productCode?: string;
  productImage?: string | null;
  pricePerMetre: string;
  // Optional breakdown for transparency (variant modifier applied at add time)
  basePricePerMetre?: string;
  priceModifier?: number;

  // Selected option (currently just one size/variant, extensible)
  variantLabel?: string;
  widthMm?: number;
  heightMm?: number;

  // Quantities & pricing
  quantity: number;
  metres: number;

  // Gang sheet
  requiresGangSheet: boolean;
  allowManualUpload: boolean;
  gangSheetLayoutId?: string | null;
  gangSheetPreviewUrl?: string | null;
  gangSheetSavedAt?: string | null;
  gangSheetName?: string | null;
  uploadedArtworkUrl?: string | null;
  uploadedArtworkName?: string | null;

  // Optional extras
  cuttingTierId?: string | null;
  cuttingTierName?: string | null;
  shippingMethodId?: string | null;
  shippingMethodName?: string | null;

  // Notes
  notes?: string;

  // Tax snapshot (captured at add-to-cart time)
  taxUseGlobal?: boolean;
  taxRateOverride?: number | null; // percentage value, e.g. 20 for 20%

  // Timestamps
  addedAt: string;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  addItem: (item: Omit<CartItem, "id" | "addedAt">) => string;
  removeItem: (id: string) => void;
  updateItem: (id: string, updates: Partial<CartItem>) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  getSubtotal: () => number;
  getItemCount: () => number;
}

function lineTotal(item: CartItem): number {
  const price = parseFloat(item.pricePerMetre || "0") || 0;
  return price * (item.metres || 0) * (item.quantity || 1);
}

export const useCart = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: (item) => {
        const id = `cart_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const now = new Date().toISOString();
        set((state) => ({
          items: [...state.items, { ...item, id, addedAt: now }],
          isOpen: true,
        }));
        return id;
      },

      removeItem: (id) => set((state) => ({ items: state.items.filter((i) => i.id !== id) })),

      updateItem: (id, updates) =>
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? { ...i, ...updates } : i)),
        })),

      clearCart: () => set({ items: [] }),
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set((s) => ({ isOpen: !s.isOpen })),

      getSubtotal: () => get().items.reduce((sum, i) => sum + lineTotal(i), 0),
      getItemCount: () => get().items.reduce((sum, i) => sum + (i.quantity || 1), 0),
    }),
    {
      name: "flightdeck-cart-v1",
    },
  ),
);

export function cartItemLineTotal(item: CartItem): number {
  return lineTotal(item);
}
